import "./document-viewer-flyout-mobile.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { ListView } from "@tql/ui-components-react/kendo/listview";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { PDFViewer } from "@tql/ui-components-react/tql/pdf-viewer";
import type { ComponentProps } from "react";
import { memo } from "react";

import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";

import { BaseFlyout } from "../flyout/base-flyout.tsx";
import { LoadingSpinner } from "../loading-spinner/loading-spinner.tsx";
import type { DocumentViewerFlyoutProps } from "./document-viewer-flyout.tsx";

type PdfViewerTools = NonNullable<ComponentProps<typeof PDFViewer>["tools"]>;

export const DocumentViewerFlyoutMobile = memo(
	({
		viewerState,
		closeViewer,
		documents,
		title = "Documents",
		contentData,
		isLoadingDocumentList,
		isLoadingInvoiceDocument,
		isDownloading,
		isPrinting,
		extraHeaderActions,
		itemRenderer,
		handlePrintClick,
		handleDownloadClick,
		isNoneSelected,
		isMobile,
	}: DocumentViewerFlyoutProps) => {
		const DEFAULT_ZOOM_LEVEL = 0.5;

		const selectPanThreshold = useIsMobile(560);
		const zoomToolThreshHold = useIsMobile(460);

		let pdfViewerTools: PdfViewerTools = [
			"pager",
			"zoomInOut",
			"zoom",
			"selection",
		];

		if (zoomToolThreshHold) {
			pdfViewerTools = ["pager", "zoomInOut"];
		} else if (selectPanThreshold) {
			pdfViewerTools = ["pager", "zoomInOut", "zoom"];
		}

		return (
			<BaseFlyout
				isMobile={isMobile}
				isOpen={viewerState.isOpen}
				onClose={closeViewer}
				panelClassName={"flyout-panel-mobile"}
				title={title}
				width={"100vw"}
			>
				<div className={"flyout-mobile-actions-row"}>
					{extraHeaderActions ? (
						<div className={"flyout-mobile-extra-actions"}>
							{extraHeaderActions}
						</div>
					) : null}
					<Button
						disabled={
							isNoneSelected ||
							(isDownloading ?? false) ||
							(isPrinting ?? false)
						}
						id={"borderless-button"}
						onClick={handleDownloadClick}
					>
						{isDownloading ? "Downloading..." : "Download Selected"}
					</Button>
					<Button
						disabled={
							isNoneSelected ||
							(isDownloading ?? false) ||
							(isPrinting ?? false)
						}
						id={"borderless-button"}
						onClick={handlePrintClick}
					>
						{isPrinting ? "Preparing..." : "Print Selected"}
					</Button>
				</div>
				<div className={"flyout-mobile-content-wrapper"}>
					<div className={"flyout-mobile-listview-wrapper"}>
						{isLoadingDocumentList ? (
							<LoadingSpinner />
						) : (
							<ListView
								data={documents ?? []}
								item={itemRenderer}
								style={{
									height: "100%",
									backgroundColor: "var(--kendo-color-base)",
								}}
							/>
						)}
					</div>
					<div className={"flyout-mobile-pdf-viewer-wrapper"}>
						{isLoadingInvoiceDocument ? (
							<div className={"flyout-mobile-loader-wrapper"}>
								<Loader
									size={"large"}
									themeColor={"primary"}
									type={"infinite-spinner"}
								/>
							</div>
						) : (
							<PDFViewer
								data={contentData || ""}
								defaultZoom={DEFAULT_ZOOM_LEVEL}
								style={{ width: "100%", height: "100%", maxWidth: "100%" }}
								tools={pdfViewerTools}
							/>
						)}
					</div>
				</div>
			</BaseFlyout>
		);
	},
);

DocumentViewerFlyoutMobile.displayName = "DocumentViewerFlyoutMobile";
